import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../lib/api';

const initialState = {
  title: '',
  summary: '',
  description: '',
  audience: 'both',
  price: '',
  video_url: '',
  is_published: true,
};

export default function ResourceForm() {
  const { id } = useParams();
  const isEdit = useMemo(() => Boolean(id), [id]);
  const navigate = useNavigate();

  const [form, setForm] = useState(initialState);
  const [coverImage, setCoverImage] = useState(null);
  const [downloadFile, setDownloadFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      loadResource();
    }
  }, [isEdit]);

  const loadResource = async () => {
    try {
      const res = await api.get(`/admin/resources/${id}`);
      const data = res.data?.data;

      setForm({
        title: data.title || '',
        summary: data.summary || '',
        description: data.description || '',
        audience: data.audience || 'both',
        price: data.price || '',
        video_url: data.video_url || '',
        is_published: Boolean(data.is_published),
      });
    } catch (err) {
      alert(err.response?.data?.message || 'Unable to load resource');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = new FormData();
      payload.append('title', form.title);
      payload.append('summary', form.summary);
      payload.append('description', form.description);
      payload.append('audience', form.audience);
      payload.append('price', form.price || 0);
      payload.append('video_url', form.video_url || '');
      payload.append('is_published', form.is_published ? '1' : '0');

      if (coverImage) payload.append('cover_image', coverImage);
      if (downloadFile) payload.append('download_file', downloadFile);
      if (videoFile) payload.append('video_file', videoFile);

      if (isEdit) {
        await api.post(`/admin/resources/${id}`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/admin/resources', payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      navigate('/dashboard/admin/resources');
    } catch (err) {
      alert(err.response?.data?.message || 'Unable to save resource');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h1 className="text-3xl font-black text-text">
          {isEdit ? 'Edit Resource' : 'Create Resource'}
        </h1>
        <p className="mt-2 text-muted">
          Upload downloadable files, videos, or protected links for workers, customers, or both.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="rounded-[2rem] border border-black/5 bg-white p-6 shadow-sm space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-bold text-text">Title</label>
            <input
              name="title"
              value={form.title}
              onChange={handleChange}
              className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-primary"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-bold text-text">Summary</label>
            <textarea
              name="summary"
              value={form.summary}
              onChange={handleChange}
              rows={3}
              className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-primary"
            />
          </div>

          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-bold text-text">Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={5}
              className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-bold text-text">Audience</label>
            <select
              name="audience"
              value={form.audience}
              onChange={handleChange}
              className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-primary"
            >
              <option value="nanny">Workers</option>
              <option value="customer">Customers</option>
              <option value="both">Both</option>
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-bold text-text">Price (NGN)</label>
            <input
              type="number"
              name="price"
              value={form.price}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-primary"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-bold text-text">Cover Image</label>
            <input
              type="file"
              onChange={(e) => setCoverImage(e.target.files?.[0] || null)}
              className="w-full rounded-2xl border border-gray-200 px-4 py-3"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-bold text-text">Downloadable File</label>
            <input
              type="file"
              onChange={(e) => setDownloadFile(e.target.files?.[0] || null)}
              className="w-full rounded-2xl border border-gray-200 px-4 py-3"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-bold text-text">Video File</label>
            <input
              type="file"
              onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
              className="w-full rounded-2xl border border-gray-200 px-4 py-3"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-bold text-text">Video URL</label>
            <input
              type="url"
              name="video_url"
              value={form.video_url}
              onChange={handleChange}
              placeholder="https://..."
              className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-primary"
            />
          </div>
        </div>

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            name="is_published"
            checked={form.is_published}
            onChange={handleChange}
            className="h-5 w-5 rounded border-gray-300 text-primary"
          />
          <span className="text-sm font-medium text-text">Publish this resource immediately</span>
        </label>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={loading}
            className="rounded-2xl bg-primary px-6 py-3 text-sm font-bold text-white disabled:opacity-60"
          >
            {loading ? 'Saving...' : isEdit ? 'Update Resource' : 'Create Resource'}
          </button>

          <button
            type="button"
            onClick={() => navigate('/dashboard/admin/resources')}
            className="rounded-2xl border border-gray-200 bg-white px-6 py-3 text-sm font-bold text-text"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}